import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { useAxios } from "@/composables/api.js";
import { useAppStore } from "./app.js";
import { useCampaignStore } from "./campaign.js";
import { useCompanyStore } from "./company.js";
import { useParticipantStore } from "./participant.js";
import { useSpaceStore } from "./space.js";
import { setToken, removeToken } from "@/composables/token.js";
import { appPermissions } from "@/acl/index.js";
import { catchError } from "@/composables/error.js";

export const useUserStore = defineStore("user", () => {
  // Init stores
  const appStore = useAppStore();
  const campaignStore = useCampaignStore();
  const companyStore = useCompanyStore();
  const participantStore = useParticipantStore();
  const spaceStore = useSpaceStore();

  const user = ref({});
  const auth_token = ref(false);

  const user_role = computed(() => {
    if (!companyStore.currentCompany) return null;

    return companyStore.currentCompany.role;
  });

  if (localStorage.getItem("auth_token")) {
    setToken(localStorage.getItem("auth_token"), "auth_token", auth_token);
  }

  function setUser(userObject) {
    Object.assign(user.value, userObject);
  }

  function hasPermission(name, value) {
    if (user_role.value === null) return;

    const user_permissions = appPermissions[user_role.value];

    return user_permissions[name] >= value;
  }

  async function getUser(fallbackRoute, company_id) {
    if (Date.now() >= auth_token.value.exp * 1000) {
      removeToken("auth_token", auth_token);
      this.$router.push({ name: fallbackRoute });
      return;
    }

    try {
      const { data } = await useAxios.get("/users-sessions");

      setUser(data.user);

      data.companies.forEach((company) => {
        const alreadyIn = companyStore.companies.find(
          (companyObj) => companyObj.company_id === company.company_id
        );

        if (!alreadyIn) companyStore.companies.push(company);
      });

      await companyStore.getCompany(company_id || data.companies[0].company_id);

      return data;
    } catch (error) {
      removeToken("auth_token", auth_token);
      this.$router.push({ name: fallbackRoute });
      return catchError(error);
    }
  }

  async function login(email, password) {
    try {
      const { data } = await useAxios.post("/users-sessions", {
        email,
        password,
      });

      setUser(data.user);

      setToken(data.token, "auth_token", auth_token);

      if (data.companies.length === 0) {
        return { error: "ERROR_NO_COMPANY" };
      }

      companyStore.companies = data.companies;

      await companyStore.getCompany(data.companies[0].company_id);

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function register() {
    try {
      const { data } = await useAxios.post("/users", {
        email: user.value.email,
        first_name: user.value.first_name,
        last_name: user.value.last_name,
        password: user.value.password,
        phone_number: user.value.phone_number,
      });

      setToken(data.token, "auth_token", auth_token);

      setUser(data.user);

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function registerFromMember(invitation_id) {
    try {
      const { data } = await useAxios.post(
        `/companies-members/${invitation_id}`,
        {
          email: user.value.email,
          first_name: user.value.first_name,
          last_name: user.value.last_name,
          password: user.value.password,
          phone_number: user.value.phone_number,
        }
      );

      setToken(data.token, "auth_token", auth_token);

      setUser(data.user);

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function joinNewCompany(invitation_id) {
    try {
      const { data } = await useAxios.put(
        `/companies-members/${invitation_id}`
      );

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function validateEmail(code) {
    try {
      const { data } = await useAxios.post("/emails/codes", {
        code,
      });

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function resendEmailCode(email) {
    try {
      const { data } = await useAxios.post("/users-emails-verifications", {
        email,
      });

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function forgotPassword(email) {
    try {
      const { data } = await useAxios.post("/users-passwords-resets", {
        email,
      });

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function resetPassword(reset_id, password) {
    try {
      const { data } = await useAxios.put(
        `/users-passwords-resets/${reset_id}`,
        {
          password,
        }
      );

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function updateUserPicture(image_id) {
    try {
      const { data } = await useAxios.put("/users/me/image", {
        image_id,
      });

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function updatePassword(previous_password, new_password) {
    try {
      const { data } = await useAxios.put("/users/me/password", {
        previous_password,
        new_password,
      });

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  async function contact(company_id, message) {
    try {
      const { data } = await useAxios.post(
        `/companies/${company_id}/contact-form`,
        {
          message,
        }
      );

      return data;
    } catch (error) {
      return catchError(error);
    }
  }

  function clear() {
    user.value = {};
    removeToken("auth_token", auth_token);
  }

  function logout() {
    const clearFuncs = [
      campaignStore.clear,
      companyStore.clear,
      spaceStore.clear,
      participantStore.clear,
      clear,
    ];

    clearFuncs.forEach((fn) => fn());

    appStore.toggleSidebar(false);
    appStore.toggleSidebarFooter(false);

    this.$router.push({ name: "loginPage" });
  }

  return {
    auth_token,
    user,
    user_role,
    hasPermission,
    setUser,
    getUser,
    login,
    logout,
    register,
    registerFromMember,
    validateEmail,
    joinNewCompany,
    resendEmailCode,
    forgotPassword,
    resetPassword,
    updateUserPicture,
    updatePassword,
    contact,
  };
});
